/// <reference types="vite/client" />
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  Outlet,
  createRootRouteWithContext,
  HeadContent,
  Scripts,
} from '@tanstack/react-router';
import { useState, useEffect, type ReactNode } from 'react';

import appCss from '~/styles.css?url';
import { GlobalSessionExpiredNotice } from '~/components/ui/SessionExpiredNotice';
import { YakSplashScreen } from '~/components/ui/YakSplashScreen';

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1, viewport-fit=cover',
      },
      { title: 'Yak Network - Play' },
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
    ],
  }),
  component: RootComponent,
});

function RootComponent() {
  // TanStack Start SPA mode prerenders a shell with empty Suspense boundaries.
  // To prevent hydration mismatch, render consistent content (YakSplashScreen)
  // during both SSR and initial client render, then show actual routes after mount.
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return (
    <RootDocument>
      {isHydrated ? (
        <>
          <GlobalSessionExpiredNotice />
          <Outlet />
        </>
      ) : (
        <YakSplashScreen />
      )}
    </RootDocument>
  );
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  const { queryClient } = Route.useRouteContext();

  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body className="min-h-screen bg-white antialiased">
        <QueryClientProvider client={queryClient}>
          {children}
        </QueryClientProvider>
        <Scripts />
      </body>
    </html>
  );
}
